import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "../../../Routes/paths";

import { ChartLegend, PopoverCard } from "../../../Components/Atoms";
import { ModelPicker, ReactTable } from "../../../Components/Molecules";
import { Stack } from "../../../Components/Organisms/Graphs";
import { Report } from "../../../Templates";

import { getStatsData, getVehicleStats, getWidgetData } from "../../../APIs";
import { StoreContext } from "../../../Library/contexts";
import { DATE_RANGES } from "../../../Library/constants";
import { MODULE_TYPES, processReportData } from "../../../Library/widgets";

import { IconButton } from "@chakra-ui/react";
import { DownloadIcon } from "../../../Assets/images/svgs";
import { CHARGING_CYCLE_HEADERS } from "../../../Library/tableHeaders";
import { getUserAndVehicle } from "../../../Library/helpers";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";

const labelData = [ "0-25%", "25-50%", "50-75%", "75-100%" ];
const parser = { x: "model", y: "count", xLabel: "Vehicles Count", yLabel: "Charging Cycles Completed" };
const paths = [
  { idx: 0, name: "Analytics", link: PATHS.ANALYTICS },
  { idx: 1, name: "Charging Cycle", isCurrentPage: true },
];

const processData = (data, constants) => {
  // For Grouping Counts >= 5
  const graphs = [];
  const legends = [];

  data.forEach((d) => {
    let counts = d.count.map(({ count }) => count);

    graphs.push({
      color: constants.modelMap[d.model].color,
      model: constants.modelMap[d.model].nm,
      count: counts,
      modelId: d.model,
    });

    legends.push({ label: constants.modelMap[d.model].nm, color: constants.modelMap[d.model].color });
  });

  return { graphs, legends };
};

const ChargeCycle = () => {
  const navigate = useNavigate();
  const { filters } = useContext(StoreContext);

  const reportRef = useRef(null);
  const widgetData = useRef(null);
  const reportData = useRef({});
  const tableData = useRef(null);

  const [ isLoading, setIsLoading ] = useState(true);
  const [ showOverlay, setShowOverlay ] = useState(false);

  const handleFetchData = useCallback(async (filters, constants) => {
    setIsLoading(true);
    let categories = [];

    if (!widgetData.current) {
      // Used to Fetch Switcher Options
      const widgetData = await getWidgetData(DATE_RANGES[filters.range], [MODULE_TYPES.CHARGING_CYCLE]);
      if (!widgetData) return;

      categories = widgetData[0].data;
      widgetData.current = categories;
      reportData.current = {};
      tableData.current = null;

      const response = await getStatsData(DATE_RANGES[filters.range], MODULE_TYPES.CHARGING_CYCLE);
      const grouped = processReportData(response, "dst");   // Grouped Model-wise
      reportData.current = processData(grouped, constants);
    }

    if (!tableData.current) {
      const response = await getVehicleStats(DATE_RANGES[filters.range], MODULE_TYPES.CHARGING_CYCLE);
      tableData.current = getUserAndVehicle(response);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (reportRef.current) reportRef.current.apply();
  }, [ handleFetchData ]);

  const getFilteredData = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  const handleClick = (row, col) => {
    const newPaths = [ ...paths ];
    newPaths[paths.length - 1] = {
      ...newPaths[paths.length - 1],
      link: PATHS.ANALYTICS + "/" + PATHS.CHARGE_CYCLE,
      isCurrentPage: false,
    };
    newPaths.push({ idx: 2, name: "Data", isCurrentPage: true });

    const graphData = getFilteredData(reportData.current.graphs);

    const state = {
      model: graphData[col].modelId,
      fault: MODULE_TYPES.CHARGING_CYCLE,
      title: "Charging Cycle - Data",
      paths: newPaths,
    };

    navigate(PATHS.REPORTS, { state });
  };

  const removePopover = (toggle) => {
    if (toggle) setShowOverlay((old) => !old);
    else setShowOverlay(false);
  };

  const columns = React.useMemo(() => Object.values(CHARGING_CYCLE_HEADERS).map(header => {

    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => (header.popoverCard && row.value && row.value.length) ? (
        <PopoverCard
          data={row.value.length}
          cardTitle="Dates of Threshold crossed"
          array={row.value}
          removePopover={removePopover}
          dateTime={true}
        />
      ) : (header.showPerc ? `${row.value}%` : row.value) || "-",
    };
  }), []);

  const downloadData = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((vehicle, idx) => {
      vehicle["Sr No"] = idx + 1;
      vehicle["Vehicle ID"] = vehicle.idx;
      vehicle["Device ID"] = vehicle.did;
      vehicle["Vehicle Make"] = vehicle.vmk;
      vehicle["Vehicle Model"] = vehicle.mo;
      vehicle["City"] = vehicle.user.cName;
      vehicle["State"] = vehicle.user.sName;
      vehicle["User ID"] = vehicle.uid;
      vehicle["User Name"] = `${vehicle.user.fn} ${vehicle.user.ln}`;
      vehicle["User Mob"] = vehicle.user.mob;
      vehicle["User Email"] = vehicle.user.eml;
      vehicle["Cycle Completed"] = `${vehicle.stat.data}%`;
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Vehicle ID, Device ID, Vehicle Make, Vehicle Model, City, State, Cycle Completed, User ID, User Name, User Mob, User Email";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Charging_Cycle_" + Date.now(), false);
  }

  return (
    <Report breadcrumb={paths} title={"Charging Cycle"} isLoading={isLoading} onApply={handleFetchData} ref={reportRef}>
      {!isLoading && (
        <>
          <div className="panel-chart-header">
            <ChartLegend data={reportData.current.legends} />
            <ModelPicker />
          </div>

          <div className="panel-chart-main">
            <label className="panel-chart-label">
              Charging Cycles Completed, All Vehicles
            </label>

            <Stack data={getFilteredData(reportData.current.graphs)} labels={labelData} parser={parser} title="Charge Cycle" onClick={handleClick} />
          </div>

          <div className="panel-chart-sheet">
            <div className="panel-chart-sheet-header">
              <IconButton
                className="panel-button"
                aria-label='Download Report'
                icon={<DownloadIcon />}
                onClick={() => downloadData(tableData.current)}
              />
            </div>
            <div className="panel-chart-table">
              <ReactTable
                columns={columns}
                data={tableData.current}
                showSearch={true}
                showOverlay={showOverlay}
              />
            </div>
          </div>
        </>
      )}
    </Report>
  );
}

export default ChargeCycle;
